import React from 'react';
import { BreadcrumbDropdown, type SelectOption } from '@chatsdet/ui';
import { useBreadcrumbs, type BreadcrumbItem } from '../contexts/BreadcrumbsContext';
import {
  useOrgPropertySelection,
  type UseOrgPropertySelectionOptions,
} from './useOrgPropertySelection';

type PropertySummary = {
  id: string;
  name: string;
  baseUrl?: string | null;
  description?: string | null;
};

type UsePropertyBreadcrumbsOptions = {
  pageBreadcrumb?: BreadcrumbItem;
  extraItems?: BreadcrumbItem[];
  onSelectProperty?: (propertyId: string) => void;
  onPropertyChange?: UseOrgPropertySelectionOptions['onPropertyChange'];
  propertyBreadcrumbVariant?: 'dropdown' | 'label';
  /**
   * Feeds the dropdown (and the label-variant's active-property lookup) from
   * a caller-scoped list instead of the full org property list this hook
   * would otherwise render. Use when a page only cares about a subset of org
   * properties — e.g. Releases only lists properties with a Release QA
   * config, not every application in the org.
   *
   * This only replaces the *displayed* list — `useOrgPropertySelection`
   * still fetches `/org/properties` unconditionally underneath, and still
   * reconciles *selection* (`propertyId`) against that unscoped list, not
   * the override. That means the shared store's own fallback can select a
   * property outside the caller's scoped list at any time (e.g. on org
   * switch), so callers must reconcile `propertyId` against their own route
   * param themselves — see `ReleasePropertyTimeline`'s effect that calls
   * `selectProperty(propertyId)` whenever the two disagree.
   */
  propertiesOverride?: { items: PropertySummary[]; isLoading: boolean };
};

export const usePropertyBreadcrumbs = (options: UsePropertyBreadcrumbsOptions = {}) => {
  const {
    pageBreadcrumb,
    extraItems,
    onSelectProperty,
    onPropertyChange,
    propertyBreadcrumbVariant = 'dropdown',
    propertiesOverride,
  } = options;

  const selection = useOrgPropertySelection({ onPropertyChange });
  const { propertyId, selectProperty, propertiesQuery } = selection;

  const properties = (propertiesOverride?.items ?? propertiesQuery.data?.items ?? []) as PropertySummary[];
  const propertiesLoading = propertiesOverride
    ? propertiesOverride.isLoading
    : propertiesQuery.isLoading;

  const activeProperty = React.useMemo(() => {
    if (!propertyId) {
      return null;
    }
    return properties.find((prop) => prop.id === propertyId) ?? null;
  }, [propertyId, properties]);

  const propertyDropdownPlaceholder = React.useMemo(() => {
    if (propertiesLoading) return 'Loading...';
    if (properties.length === 0) return 'No applications';
    return 'Select application';
  }, [propertiesLoading, properties.length]);

  const handleSelectProperty = React.useCallback(
    (nextId: string) => {
      if (!nextId || nextId === propertyId) {
        return;
      }
      selectProperty(nextId);
      onSelectProperty?.(nextId);
    },
    [onSelectProperty, propertyId, selectProperty]
  );

  const dropdownItems = React.useMemo<SelectOption[]>(
    () => properties.map((property) => ({ id: property.id, label: property.name })),
    [properties]
  );

  const propertyDropdownNode = React.useMemo(() => {
    if (propertyBreadcrumbVariant === 'label') {
      return null;
    }
    return (
      <BreadcrumbDropdown
        items={dropdownItems}
        selectedId={propertyId}
        onSelectItem={handleSelectProperty}
        placeholder={propertyDropdownPlaceholder}
        isDisabled={propertiesLoading || dropdownItems.length === 0}
      />
    );
  }, [
    dropdownItems,
    handleSelectProperty,
    propertyBreadcrumbVariant,
    propertyDropdownPlaceholder,
    propertiesLoading,
    propertyId,
  ]);

  const breadcrumbItems = React.useMemo(() => {
    const items: BreadcrumbItem[] = [];
    if (pageBreadcrumb) {
      items.push(pageBreadcrumb);
    }
    if (propertyBreadcrumbVariant === 'label') {
      items.push({
        key: 'property-static',
        label: activeProperty?.name ?? (propertiesLoading ? 'Loading application…' : 'Application'),
      });
    } else if (propertyDropdownNode) {
      items.push({ key: 'property-selector', node: propertyDropdownNode });
    }
    if (extraItems && extraItems.length > 0) {
      items.push(...extraItems);
    }
    return items;
  }, [
    activeProperty?.name,
    extraItems,
    pageBreadcrumb,
    propertiesLoading,
    propertyBreadcrumbVariant,
    propertyDropdownNode,
  ]);

  useBreadcrumbs(breadcrumbItems);

  return {
    propertyId,
    selectProperty,
    propertiesQuery,
    propertyOptions: dropdownItems,
    activeProperty,
    properties,
  };
};
